/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { LogoFragment } from "@cosmos/hygraph-schema";
import { forwardRef } from "react";

import { css, cva, RecipeVariantProps } from "@panda/css";
import { Box } from "@panda/jsx";
import { flex } from "@panda/patterns";

import { Logo } from "./Logo.atom";

export type LogoListProps = {
	logos?: LogoFragment[] | null;
	height?: number;
	marquee?: boolean;
	gridLayout?: boolean | null;
	delay?: boolean;
};

const duration = 120;

const logoGrid = cva({
	base: {
		"&&": {
			display: "grid",
			columnGap: "eds.dimension.64",
			rowGap: "eds.dimension.64",
		},
	},
	variants: {
		logos: {
			1: {
				gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
			},
			2: {
				gridTemplateColumns: {
					base: "repeat(2, minmax(0, 1fr))",
				},
			},
			3: {
				gridTemplateColumns: {
					base: "repeat(3, minmax(0, 1fr))",
					mdDown: "repeat(2, minmax(0, 1fr))",
				},
			},
			4: {
				gridTemplateColumns: {
					base: "repeat(4, minmax(0, 1fr))",
					xlDown: "repeat(3, minmax(0, 1fr))",
					mdDown: "repeat(2, minmax(0, 1fr))",
				},
			},
			5: {
				gridTemplateColumns: {
					base: "repeat(5, minmax(0, 1fr))",
					xlDown: "repeat(3, minmax(0, 1fr))",
					mdDown: "repeat(2, minmax(0, 1fr))",
				},
			},
		},
	},
	defaultVariants: {
		logos: 5,
	},
});

/**
 * LogoList is expected to be used within LogoMarquee, it is missing styles to be used on its own.
 * Sets the height for all of the logos.
 */
export const LogoList = forwardRef<any, LogoListProps>(
	(
		{ logos = [], height = 54, marquee = false, gridLayout = false, delay = false }: LogoListProps,
		forwardedRef,
	): React.JSX.Element | null => {
		if (!logos) return null;
		const logoCount = logos?.length ?? 0;

		return (
			<Box
				ref={forwardedRef}
				direction="row"
				alignItems="center"
				columnGap="eds.dimension.64"
				paddingInline="eds.dimension.32"
				rowGap="eds.dimension.64"
				flexWrap={marquee ? "nowrap" : "wrap"}
				justifyContent={marquee ? "flex-start" : "center"}
				className={`logo-list ${css({
					animationTimingFunction: "linear",
					animationIterationCount: "infinite",
					position: gridLayout ? "relative" : "absolute",
					overflow: "hidden",
					w: "auto",
					animationName: marquee ? "marquee" : "",
					animationDuration: `${duration}s`,
					animationDelay: delay ? `-${duration / 2}s` : "",
				})}
				${gridLayout ? logoGrid({ logos: logoCount > 4 ? 5 : logoCount } as RecipeVariantProps<typeof logoGrid>) : flex()}`}
			>
				{logos.map((logo, i) => (
					<div
						key={`logo-${logo.title}-${i}`}
						className={`logo-list-wrap ${css({
							display: "inline-block",
							height: `${height}px`,
						})}`}
					>
						<Logo {...logo} height={height} />
					</div>
				))}
			</Box>
		);
	},
);

LogoList.displayName = "LogoList";
